import React from "react";

const METRICS = [
  { key: "shared_actions", label: "Shared" },
  { key: "viewed_actions", label: "Viewed" },
  { key: "call_actions", label: "Call" },
  { key: "interested_actions", label: "Interested" },
  { key: "meeting_planned_actions", label: "Meeting Planned" },
  { key: "meeting_done_actions", label: "Meeting Done" },
  { key: "rejected_actions", label: "Rejected" },
  { key: "cancelled_actions", label: "Cancelled" },
  { key: "admin_notes_added", label: "Admin Notes" },
];

export default function AssignedProfilePerformanceModal({
  open,
  loading,
  error,
  data,
  onClose,
}) {
  if (!open) return null;

  const profile = data?.profile || null;
  const assignedUser = data?.assigned_user || null;
  const summary = data?.summary || {};
  const recommendationHistory = safeArray(data?.recommendation_history);
  const noteHistory = safeArray(data?.note_history);

  return (
    <div style={S.backdrop} onClick={onClose}>
      <div style={S.shell} onClick={(event) => event.stopPropagation()}>
        <div style={S.header}>
          <div>
            <div style={S.eyebrow}>Assigned Profile Performance</div>
            <h2 style={S.title}>
              {profile?.profile_no ? `#${profile.profile_no} - ` : ""}
              {profile?.full_name || "Assigned profile"}
            </h2>
            <div style={S.subline}>
              {assignedUser?.email || "Assigned user"}
              {profile?.gender || profile?.city || profile?.state
                ? ` | ${[profile?.gender, profile?.city, profile?.state].filter(Boolean).join(" - ")}`
                : ""}
            </div>
          </div>
          <button type="button" style={S.closeBtn} onClick={onClose}>
            x
          </button>
        </div>

        {loading ? (
          <div style={S.stateBox}>Loading performance snapshot...</div>
        ) : error ? (
          <div style={S.errorBox}>{error}</div>
        ) : (
          <>
            <div style={S.metricGrid}>
              {METRICS.map((metric) => (
                <div key={metric.key} style={S.metricCard}>
                  <div style={S.metricLabel}>{metric.label}</div>
                  <div style={S.metricValue}>{Number(summary?.[metric.key] || 0)}</div>
                </div>
              ))}
            </div>

            <div style={S.snapshotGrid}>
              <div style={S.snapshotCard}>
                <div style={S.snapshotLabel}>Last Recommendation Action</div>
                <div style={S.snapshotValue}>
                  {formatDateTime(summary?.last_recommendation_action_at)}
                </div>
              </div>
              <div style={S.snapshotCard}>
                <div style={S.snapshotLabel}>Last Admin Note</div>
                <div style={S.snapshotValue}>
                  {formatDateTime(summary?.last_admin_note_at)}
                </div>
              </div>
            </div>

            <div style={S.contentGrid}>
              <HistorySection
                title="Recommendation History"
                emptyText="No recommendation actions recorded by this user on this profile yet."
                items={recommendationHistory}
                renderItem={(item) => (
                  <div key={item.history_id} style={S.historyRow}>
                    <div style={S.historyTop}>
                      <span style={{ ...S.statusPill, ...statusPillStyle(item.action_status) }}>
                        {formatStatusLabel(item.action_status)}
                      </span>
                      <span style={S.historyDate}>{formatDateTime(item.action_at)}</span>
                    </div>
                    <div style={S.historyHeadline}>
                      {item.history_kind === "recommendation" ? "Shared with" : "Updated for"}{" "}
                      {item.to_profile_no ? `#${item.to_profile_no} - ` : ""}
                      {item.to_profile_name || "Unknown profile"}
                    </div>
                    <div style={S.historyMeta}>
                      {item.outcome_source ? `Source: ${formatStatusLabel(item.outcome_source)}` : "Initial recommendation"}
                      {item.contact_side ? ` | For: ${formatStatusLabel(item.contact_side)}` : ""}
                    </div>
                    {item.details_note ? <div style={S.historyBody}>{item.details_note}</div> : null}
                  </div>
                )}
              />

              <HistorySection
                title="Admin Note History"
                emptyText="No admin notes recorded by this user on this profile yet."
                items={noteHistory}
                renderItem={(item) => (
                  <div key={item.id} style={S.historyRow}>
                    <div style={S.historyTop}>
                      <span style={S.noteTypePill}>{formatStatusLabel(item.note_type)}</span>
                      <span style={S.historyDate}>{formatDateTime(item.created_at)}</span>
                    </div>
                    <div style={S.historyMeta}>
                      Outcome: {formatStatusLabel(item.outcome || "none")}
                      {item.follow_up_status_effective
                        ? ` | Follow-up: ${formatStatusLabel(item.follow_up_status_effective)}`
                        : ""}
                    </div>
                    <div style={S.historyBody}>{item.content || "No note content captured."}</div>
                  </div>
                )}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function HistorySection({ title, items, emptyText, renderItem }) {
  return (
    <section style={S.historyCard}>
      <div style={S.sectionTitle}>{title}</div>
      {items.length === 0 ? (
        <div style={S.emptyHistory}>{emptyText}</div>
      ) : (
        <div style={S.historyList}>{items.map(renderItem)}</div>
      )}
    </section>
  );
}

function formatDateTime(value) {
  if (!value) return "No activity yet";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No activity yet";
  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatStatusLabel(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized || normalized === "none") return "None";
  return normalized
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function statusPillStyle(status) {
  const normalized = String(status || "").trim().toLowerCase();
  if (normalized === "rejected" || normalized === "cancelled") {
    return {
      background: "#fff2ef",
      color: "#D73C2E",
      borderColor: "rgba(185,52,58,0.20)",
    };
  }
  if (normalized === "meeting_done" || normalized === "interested") {
    return {
      background: "#f2fff7",
      color: "#18794e",
      borderColor: "rgba(24,121,78,0.18)",
    };
  }
  return {
    background: "#fff8ef",
    color: "#8a5717",
    borderColor: "rgba(183,121,31,0.18)",
  };
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

const S = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(37,41,53,0.48)",
    display: "grid",
    placeItems: "center",
    padding: 20,
    zIndex: 1000,
  },
  shell: {
    width: "min(1220px, 100%)",
    maxHeight: "90vh",
    overflow: "auto",
    borderRadius: 30,
    background: "#fffdfc",
    border: "1px solid rgba(185,52,58,0.16)",
    boxShadow: "0 28px 80px rgba(37,41,53,0.22)",
    padding: 24,
    display: "grid",
    gap: 18,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    gap: 20,
    alignItems: "flex-start",
  },
  eyebrow: {
    color: "#D73C2E",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 2,
    textTransform: "uppercase",
    marginBottom: 8,
  },
  title: {
    margin: 0,
    color: "#252935",
    fontSize: 30,
    fontWeight: 950,
    letterSpacing: -0.7,
  },
  subline: {
    marginTop: 10,
    color: "#6b6f7d",
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.6,
  },
  closeBtn: {
    width: 42,
    height: 42,
    borderRadius: "50%",
    border: "1px solid rgba(185,52,58,0.16)",
    background: "#fff",
    color: "#252935",
    fontSize: 18,
    fontWeight: 900,
    cursor: "pointer",
    flexShrink: 0,
  },
  stateBox: {
    padding: 28,
    borderRadius: 22,
    border: "1px dashed rgba(185,52,58,0.22)",
    background: "#fff8f6",
    color: "#6b6f7d",
    textAlign: "center",
    fontWeight: 800,
  },
  errorBox: {
    padding: 18,
    borderRadius: 20,
    background: "#fff2ef",
    color: "#D73C2E",
    fontWeight: 800,
    border: "1px solid rgba(185,52,58,0.16)",
  },
  metricGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
    gap: 12,
  },
  metricCard: {
    padding: 16,
    borderRadius: 22,
    background: "#fff",
    border: "1px solid rgba(185,52,58,0.14)",
    boxShadow: "0 12px 30px rgba(37,41,53,0.05)",
  },
  metricLabel: {
    color: "#6b6f7d",
    fontSize: 11,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: 1.6,
  },
  metricValue: {
    marginTop: 8,
    color: "#252935",
    fontSize: 32,
    fontWeight: 950,
    lineHeight: 1,
  },
  snapshotGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 12,
  },
  snapshotCard: {
    padding: 16,
    borderRadius: 20,
    background: "#fff7f5",
    border: "1px solid rgba(185,52,58,0.14)",
  },
  snapshotLabel: {
    color: "#6b6f7d",
    fontSize: 12,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: 1.2,
  },
  snapshotValue: {
    marginTop: 8,
    color: "#252935",
    fontSize: 15,
    fontWeight: 800,
  },
  contentGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 16,
  },
  historyCard: {
    minWidth: 0,
    padding: 18,
    borderRadius: 24,
    background: "#fff",
    border: "1px solid rgba(185,52,58,0.14)",
    display: "grid",
    gap: 14,
  },
  sectionTitle: {
    color: "#252935",
    fontSize: 17,
    fontWeight: 900,
  },
  emptyHistory: {
    padding: 18,
    borderRadius: 18,
    background: "#fff8f6",
    color: "#6b6f7d",
    fontWeight: 700,
    textAlign: "center",
  },
  historyList: {
    display: "grid",
    gap: 12,
    maxHeight: "48vh",
    overflowY: "auto",
    paddingRight: 4,
  },
  historyRow: {
    padding: 14,
    borderRadius: 18,
    background: "#fffdfc",
    border: "1px solid rgba(185,52,58,0.12)",
    display: "grid",
    gap: 8,
  },
  historyTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
  },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 28,
    padding: "0 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 900,
    border: "1px solid transparent",
  },
  noteTypePill: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 28,
    padding: "0 10px",
    borderRadius: 999,
    background: "#fff2ef",
    color: "#D73C2E",
    border: "1px solid rgba(185,52,58,0.16)",
    fontSize: 12,
    fontWeight: 900,
  },
  historyDate: {
    color: "#6b6f7d",
    fontSize: 12,
    fontWeight: 800,
    textAlign: "right",
  },
  historyHeadline: {
    color: "#252935",
    fontSize: 15,
    fontWeight: 850,
    lineHeight: 1.5,
  },
  historyMeta: {
    color: "#6b6f7d",
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.5,
  },
  historyBody: {
    color: "#252935",
    fontSize: 13,
    lineHeight: 1.7,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
};
